import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface Account {
  type: 'portal' | 'bridge_club' | 'team';
  id: number | string;
  portal_user_id?: number;
  member_id?: number;
  customer_id?: number;
  label: string;
  subtitle: string;
  icon?: string;
  is_current?: boolean;
}

export interface UseMultiAccountResult {
  accounts: Account[];
  hasMultipleAccounts: boolean;
  loading: boolean;
  error: string | null;
  checkAccounts: () => Promise<void>;
  switchAccount: (account: Account) => Promise<void>;
}

/**
 * Hook to manage multi-account functionality.
 * Checks if the current user has access to multiple accounts
 * and provides functions to switch between them.
 */
export function useMultiAccount(): UseMultiAccountResult {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [hasMultipleAccounts, setHasMultipleAccounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/user-accounts/`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
        setHasMultipleAccounts(data.has_multiple || false);
      } else if (response.status === 401) {
        // Not authenticated - clear accounts
        setAccounts([]);
        setHasMultipleAccounts(false);
      } else {
        throw new Error('Failed to fetch accounts');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to check accounts');
      setAccounts([]);
      setHasMultipleAccounts(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const switchAccount = useCallback(async (account: Account) => {
    setLoading(true);
    setError(null);

    try {
      // Build request body for universal login
      const body: any = {
        account_type: account.type,
      };

      // For portal accounts, include portal_user_id
      if (account.portal_user_id) {
        body.portal_user_id = account.portal_user_id;
      }

      // For bridge club, include member_id
      if (account.member_id) {
        body.member_id = account.member_id;
      }

      const response = await fetch(`${API_BASE_URL}/auth/universal-login/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store appropriate user data
        if (data.user_type === 'portal') {
          localStorage.setItem('portal_user', JSON.stringify(data.user));
          localStorage.removeItem('bridge_club_member');
        } else if (data.user_type === 'bridge_club') {
          localStorage.setItem('bridge_club_member', JSON.stringify(data.member));
          localStorage.removeItem('portal_user');
        }

        // Redirect to appropriate portal
        if (data.redirect_url) {
          window.location.href = data.redirect_url;
        } else if (account.type === 'bridge_club') {
          window.location.href = '/bridge-club/dashboard';
        } else if (account.type === 'portal') {
          window.location.href = '/portal/dashboard';
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        throw new Error(data.error || 'Failed to switch account');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to switch account');
      setLoading(false);
    }
  }, []);

  // Check accounts on mount
  useEffect(() => {
    checkAccounts();
  }, [checkAccounts]);

  return {
    accounts,
    hasMultipleAccounts,
    loading,
    error,
    checkAccounts,
    switchAccount,
  };
}
